import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField,
    Autocomplete,
    IconButton,
    Grid,
    Button,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { PLATFORM_KEY_OPTIONS } from '../config/launchConfig';

const LaunchFormFields = ({
    tabValue,
    selectedServer,
    hasCredentials,
    filteredGamesList,
    gamesList,
    marketList,
    siteList,
    operatorOptions,
    brandOptions,
    languageList,
    currencyCodeOptions,
    productList,
    handleFieldChange,
    handleGameChange,
    handleMarketChange,
    handlePlatformChange,
    handleOperatorChange,
    handleLobbyChange,
    handleProductNameChange,
    handleAccountIdChange,
    handleAuthTokenChange,
    toggleServerExplorer,
    setGameDetailsOpen,
    lobbyList,
}) => {
    // Group games by display title for improved selection
    const [selectedGameTitle, setSelectedGameTitle] = useState('');
    const [selectedVariant, setSelectedVariant] = useState(null);


    // Update the useMemo logic to preserve all unique launch IDs
    // Update the useMemo logic to handle case-insensitive duplicates
    const { uniqueGameTitles, gameVariantsMap } = useMemo(() => {
        const gamesByTitle = {};
        const allGames = tabValue === 1 ? filteredGamesList : gamesList;

        allGames.forEach(game => {
            // Use gameName as the grouping key
            const title = (game.gameName || '').trim();
            if (title && game.launchID) {
                if (!gamesByTitle[title]) {
                    gamesByTitle[title] = [];
                }

                // Check if this launch ID already exists (case-insensitive)
                const duplicateIndex = gamesByTitle[title].findIndex(
                    existing => existing.launchID.toLowerCase() === game.launchID.toLowerCase()
                );

                if (duplicateIndex === -1) {
                    // No duplicate found, add the game
                    gamesByTitle[title].push(game);
                } else {
                    // Duplicate found, keep the one with better casing
                    const existing = gamesByTitle[title][duplicateIndex];

                    // Use a heuristic to determine "better" casing - prefer camelCase over lowercase
                    const existingHasProperCase = /[a-z][A-Z]/.test(existing.launchID);
                    const newHasProperCase = /[a-z][A-Z]/.test(game.launchID);

                    if (newHasProperCase && !existingHasProperCase) {
                        // Replace with the better-cased version
                        gamesByTitle[title][duplicateIndex] = game;
                    }
                }
            }
        });

        return {
            uniqueGameTitles: Object.keys(gamesByTitle).sort(),
            gameVariantsMap: gamesByTitle
        };
    }, [filteredGamesList, gamesList, tabValue]);

    // Find title for the currently selected game
    useEffect(() => {
        if (selectedServer.game) {
            // Find the title for the selected game
            for (const [title, variants] of Object.entries(gameVariantsMap)) {
                const found = variants.find(v =>
                    v.id === selectedServer.game.id ||
                    v.launchID === selectedServer.game.launchID);
                if (found) {
                    setSelectedGameTitle(title);
                    setSelectedVariant(selectedServer.game);
                    break;
                }
            }
        } else {
            setSelectedGameTitle('');
            setSelectedVariant(null);
        }
    }, [selectedServer.game, gameVariantsMap]);

    const handleGameTitleChange = (event, newTitle) => {
        // If the new title is null (clear button was clicked), reset everything
        if (newTitle === null) {
            setSelectedGameTitle('');
            setSelectedVariant(null);
            // Also clear the game selection in the parent component
            handleGameChange(event, null);
            return;
        }

        setSelectedGameTitle(newTitle);
        setSelectedVariant(null);

        // If there's only one variant, select it automatically
        const variants = gameVariantsMap[newTitle] || [];
        if (variants.length === 1) {
            handleVariantSelection(null, variants[0]);
        }
    };

    // Handle variant selection
    const handleVariantSelection = (event, variant) => {
        setSelectedVariant(variant);
        handleGameChange(event, variant);
    };

    return (
        <Grid container spacing={2}>
            {/* ROW 1 */}
            {tabValue === 1 ? (
                <>
                    {/* Game Selection for Tab 1 (GameFlex tab) */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={filteredGamesList}
                            getOptionLabel={(option) => option.launchID}
                            value={selectedServer.game}
                            onChange={handleGameChange}
                            renderInput={(params) => (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField {...params} placeholder="Game" variant="outlined" fullWidth />
                                    <IconButton
                                        onClick={() => setGameDetailsOpen(true)}
                                        disabled={!selectedServer.game}
                                    >
                                        <InfoIcon />
                                    </IconButton>
                                </div>
                            )}
                        />
                    </Grid>

                    {/* Username Field */}
                    <Grid item xs={3}>
                        <TextField
                            placeholder="UserName"
                            variant="outlined"
                            value={selectedServer.username || ''}
                            onChange={(e) => handleFieldChange('username', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Password Field */}
                    <Grid item xs={3}>
                        <TextField
                            placeholder="Password"
                            variant="outlined"
                            type="password"
                            value={selectedServer.password || ''}
                            onChange={(e) => handleFieldChange('password', e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Currency Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={currencyCodeOptions}
                            getOptionLabel={(option) => option}
                            value={selectedServer.currency?.name}
                            onChange={(e, newValue) => handleFieldChange('currency', { name: newValue })}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="CurrencyCode" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>
                </>
            ) : (
                <>
                    {/* Game Title Selection for Tab 0 */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={uniqueGameTitles}
                            value={selectedGameTitle || null}
                            onChange={handleGameTitleChange}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Game Title" variant="outlined" fullWidth />
                            )}
                            freeSolo={false}
                            disableClearable={false}
                        />
                    </Grid>

                    {/* Game Variant Selection */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={gameVariantsMap[selectedGameTitle] || []}
                            getOptionLabel={(option) => option?.launchID || ''}
                            value={selectedVariant}
                            onChange={handleVariantSelection}
                            disabled={!selectedGameTitle}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option?.launchID || ''}
                                </li>
                            )}
                            renderInput={(params) => (
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <TextField {...params} placeholder="Game Variant" variant="outlined" fullWidth />
                                    <IconButton
                                        onClick={() => setGameDetailsOpen(true)}
                                        disabled={!selectedVariant}
                                    >
                                        <InfoIcon />
                                    </IconButton>
                                </div>
                            )}
                        />
                    </Grid>

                    {/* Market Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={marketList}
                            getOptionLabel={(option) => option.externalRegMarketName}
                            value={selectedServer.market}
                            onChange={handleMarketChange}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Market" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Site Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={siteList}
                            getOptionLabel={(option) => option.name}
                            value={selectedServer.site}
                            onChange={(e, newValue) => handleFieldChange('site', newValue)}
                            disabled={!selectedServer.market} // Disable if no market is selected
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Site" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>


                </>
            )}

            {/* ROW 2 */}
            {tabValue === 1 ? (
                <>
                    {/* Platform Key Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={PLATFORM_KEY_OPTIONS.map((p) => p.key)}
                            getOptionLabel={(option) => option}
                            value={selectedServer.platformKey}
                            onChange={(event, newValue) =>
                                handlePlatformChange(event, newValue, selectedServer.username, selectedServer.password)
                            }
                            disabled={!hasCredentials}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Platform Key"
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            className="mb-4"
                        />
                    </Grid>

                    {/* Operator Code Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={operatorOptions}
                            getOptionLabel={(option) => option.label}
                            value={operatorOptions.find(op => op.value === selectedServer.operatorCode) || null}
                            onChange={(event, newValue) => handleOperatorChange(event, newValue, selectedServer.platformKey)}
                            disabled={!hasCredentials}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Operator Code"
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            className="mb-4"
                        />
                    </Grid>

                    {/* Brand Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={brandOptions}
                            getOptionLabel={(option) => option}
                            value={selectedServer.brand}
                            onChange={(e, newValue) => handleFieldChange('brand', newValue)}
                            disabled={!hasCredentials}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Brands" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Account ID Field */}
                    <Grid item xs={3}>
                        <TextField
                            placeholder="Account ID"
                            variant="outlined"
                            value={selectedServer.accountId || ''}
                            onChange={handleAccountIdChange}
                            fullWidth
                        />
                    </Grid>
                </>
            ) : (
                <>
                    {/* Server ID Field */}
                    <Grid item xs={3}>
                        <TextField
                            placeholder="Server ID"
                            variant="outlined"
                            value={selectedServer.serverId}
                            onChange={(e) => handleFieldChange('serverId', e.target.value)}
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => toggleServerExplorer(true)}
                            fullWidth
                            style={{ marginTop: '8px' }}
                        >
                            Open Server Explorer
                        </Button>
                    </Grid>

                    {/* Lobby Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={lobbyList}
                            getOptionLabel={(option) => {
                                // Handle both string and object cases
                                return typeof option === 'string' ? option : option?.name || ''
                            }}
                            value={selectedServer.lobby}
                            onChange={handleLobbyChange}
                            freeSolo
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Lobby"
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            isOptionEqualToValue={(option, value) => {
                                // Handle comparison for both string and object cases
                                if (!option || !value) return false;
                                const optionName = typeof option === 'string' ? option : option.name;
                                const valueName = typeof value === 'string' ? value : value.name;
                                return optionName === valueName;
                            }}
                        />
                    </Grid>

                    {/* Auth Token Field */}
                    <Grid item xs={3}>
                        <TextField
                            placeholder="Auth Token"
                            variant="outlined"
                            value={selectedServer.authToken}
                            onChange={handleAuthTokenChange}
                            fullWidth
                        />
                    </Grid>

                    {/* Language Field */}
                    <Grid item xs={3}>
                        <Autocomplete
                            options={languageList}
                            getOptionLabel={(option) => option.name}
                            value={selectedServer.language}
                            onChange={(e, newValue) => handleFieldChange('language', newValue)}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Language" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>

                    {/* Product Name Field (hidden in row 2) */}
                    <Grid item xs={3} style={{ display: 'none' }}>
                        <Autocomplete
                            options={productList}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedServer.productName}
                            disabled={true}
                            onChange={handleProductNameChange}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Product Name" variant="outlined" fullWidth />
                            )}
                        />
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default LaunchFormFields;